import { useEffect, useState } from 'react';

const STORAGE_PREFIX = 'pd:countdown';

interface UseCountdownTimerOptions {
  identifier: string;
  durationSeconds: number;
  urgencyThresholdSeconds?: number;
}

interface UseCountdownTimerResult {
  formattedTime: string;
  timeParts: { hours: string; minutes: string; seconds: string };
  isUrgency: boolean;
  isEnded: boolean;
}

const formatTime = (totalSeconds: number): string => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return [hours, minutes, seconds].map((v) => String(v).padStart(2, '0')).join(':');
};

const getOrSetStartTime = (key: string): number => {
  try {
    const stored = localStorage.getItem(key);
    if (stored) return parseInt(stored, 10);
    const now = Date.now();
    localStorage.setItem(key, String(now));
    return now;
  } catch {
    return Date.now();
  }
};

const getRemainingSeconds = (sk: string, durationSeconds: number): number => {
  if (typeof window === 'undefined' || durationSeconds === 0) return durationSeconds;
  const startTime = getOrSetStartTime(sk);
  const elapsed = Math.floor((Date.now() - startTime) / 1000);
  return Math.max(0, durationSeconds - elapsed);
};

/**
 * Countdown timer hook that persists start time in localStorage so the timer
 * resumes correctly across page reloads and browser sessions.
 *
 * Each `identifier` maps to an independent `pd:countdown-{identifier}` key,
 * allowing multiple timers to run in parallel on different pages.
 */
const useCountdownTimer = ({
  identifier,
  durationSeconds,
  urgencyThresholdSeconds = 0,
}: UseCountdownTimerOptions): UseCountdownTimerResult => {
  const storageKey = `${STORAGE_PREFIX}-${identifier}`;

  const [remainingSeconds, setRemainingSeconds] = useState<number>(() =>
    getRemainingSeconds(storageKey, durationSeconds)
  );

  useEffect(() => {
    if (remainingSeconds <= 0) return;

    const interval = setInterval(() => {
      setRemainingSeconds(getRemainingSeconds(storageKey, durationSeconds));
    }, 1000);

    return () => clearInterval(interval);
  }, [storageKey, remainingSeconds, durationSeconds]);

  const hours = Math.floor(remainingSeconds / 3600);
  const minutes = Math.floor((remainingSeconds % 3600) / 60);
  const seconds = remainingSeconds % 60;

  return {
    formattedTime: formatTime(remainingSeconds),
    timeParts: {
      hours: String(hours).padStart(2, '0'),
      minutes: String(minutes).padStart(2, '0'),
      seconds: String(seconds).padStart(2, '0'),
    },
    isUrgency:
      remainingSeconds > 0 &&
      urgencyThresholdSeconds > 0 &&
      remainingSeconds <= urgencyThresholdSeconds,
    isEnded: remainingSeconds <= 0,
  };
};

export default useCountdownTimer;
